import { BreadcrumbComponent, Button, CIPageHeader, Loader, useToastMessage } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';

import UploadFiles from '../../../common/components/layouts/ImportConfiguration/UploadFiles';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';
import { fetchImportServiceSummaryRequest } from '../../store/actions/sagaActions/ImportJourneySummarySagaActions';
import { AppDispatch, useSelector } from '../../store/AppStore';
import { clearImportJourneySummary } from '../../store/reducer/ImportJourneySummaryReducer';
import ImportFiles from './ImportFiles';
import classes from './ImportJourney.module.scss';

interface FileType {
  length: number,
  lastModified: number,
  name: string,
  webkitRelativePath: string,
  size: number,
  type: string,
}
const ImportJourney = () => {
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const initialFile: FileType = {
    length: 0,
    lastModified: 0,
    name: '',
    webkitRelativePath: '',
    size: 0,
    type: '',
  };

  const [uploadedFilesForJSON, setUploadedFilesForJSON] = useState(initialFile);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { success, errorMessage, isLoading } = useSelector(
    (state) => state.ImportJourneySummaryReducer
  );

  const { getRootProps: getRootJSONProps, getInputProps: getInputJSONProps, fileRejections } = useDropzone({
    accept: {
      'application/zip': ['.zip']
    },
    onDrop: (acceptedFile: DropzoneRootProps) => {
      setUploadedFilesForJSON(
        Object.assign(acceptedFile[0], {
          preview: URL.createObjectURL(acceptedFile[0])
        })
      );
    },
    noClick: uploadedFilesForJSON.length !== 0 && !(uploadedFilesForJSON.name?.replace(/.zip/g, '').includes('.'))
  });

  useEffect(() => {
    if (fileRejections.length) {
      toastMessage.warning(
        translate('journey_builder_component.error_file_upload_header'),
        translate('journey_builder_component.error_file_upload_body'),
        50000
      );
    }
  }, [fileRejections]);

  useEffect(() => {
    if (success) {
      toastMessage.success(
        translate('journey_builder_component.title_toast_success'),
        translate('journey_builder_component.msg_import_journey_config_success')
      );
      navigate('/journey-designer/journeys/import');
      setUploadedFilesForJSON(initialFile);
    }
    if (errorMessage) {
      toastMessage.failure(
        translate('journey_builder_component.title_toast_error'),
        errorMessage
      );
    }
  }, [errorMessage, success]);

  const handleNavigateToImport = async () => {
    const formData = new FormData();
    const UploadFiles: unknown = uploadedFilesForJSON;
    const uploadedFiles = UploadFiles as string | Blob;
    formData.append('zip-file', uploadedFiles);
    dispatch(fetchImportServiceSummaryRequest(formData));
  };

  const closeHandler = () => {
    navigate(journeyBuilderRoutes.viewJourney);
    dispatch(clearImportJourneySummary());
  };

  return (
    <div className={classes['import-journey-configuration-main-container']}>
      <BreadcrumbComponent />
      <Loader show={isLoading} />
      {toastMessage.renderToast()}
      <div className={classes['import-journey-button']}>
        <CIPageHeader header={translate('import_journey_configuration_component.txt_title')} />
        {success && <Button onClick={closeHandler} variant="secondary" data-testid="btn-close">{translate('multi_form_modal_component.btn_close')}</Button>}
      </div>
      <p className={classes['import-journey-configuration-sub-heading']}>{translate('import_journey_configuration_component.txt_subtitle')}</p>
      <Routes>
        <Route
          path="/upload-files"
          element={
            <UploadFiles
              uploadedFilesForJSON={uploadedFilesForJSON}
              getRootJSONProps={getRootJSONProps}
              getInputJSONProps={getInputJSONProps}
              setUploadedFilesForJSON={setUploadedFilesForJSON}
              handleNavigateToImport={handleNavigateToImport}
              path="/journey-designer/journeys"
            />
          }
        />
        <Route path="/import" element={<ImportFiles />} />
      </Routes>
    </div>
  );
};

export default ImportJourney;
